import { DataTableFilters } from "./DataTableFilters";
import { DataTableRoot } from "./DataTableRoot";
import { DataTableTable } from "./DataTableTable";
import { DataTableToolbar } from "./DataTableToolbar";

/**
 * Compound primitive for rendering a paginated, sortable, selectable data
 * table. Composed of:
 *
 * - `DataTable.Root` — outer flex column that owns layout and spacing, hosts
 *   the table, and lets sibling chrome (filters, alerts) sit alongside.
 * - `DataTable.Toolbar` — horizontal slot above the table for the filters
 *   button and other controls.
 * - `DataTable.Table` — the bordered table surface itself, including its
 *   pagination footer. Consumes the table slice from `useDataTableState`
 *   and the columns built via `createColumnHelper`.
 * - `DataTable.Filters` — filter button + popover that emits a structured
 *   `Filter[]` for the consumer to convert into a wire `Condition`.
 *
 * State and column definitions are produced outside the tree:
 * - `useDataTableState` returns a `.table` slice that spreads directly onto
 *   `DataTable.Table`.
 * - `createColumnHelper<TData>()` builds typed column definitions.
 *
 * @example
 * ```tsx
 * const columnHelper = createColumnHelper<Row>();
 * const columns = [columnHelper.accessor("name", { headerName: "Name" })];
 *
 * function MyTable({ rows }: { rows: Row[] }) {
 *   const tableState = useDataTableState({ initialSort });
 *   return (
 *     <DataTable.Root>
 *       <DataTable.Table<Row>
 *         {...tableState.table}
 *         data={rows}
 *         columns={columns}
 *         getRowId={(r) => r.id}
 *         totalRowCount={countQuery.data}
 *         noRowsContent="No rows"
 *       />
 *     </DataTable.Root>
 *   );
 * }
 * ```
 */
export const DataTable = {
  Root: DataTableRoot,
  Toolbar: DataTableToolbar,
  Table: DataTableTable,
  Filters: DataTableFilters,
};
